import {
  getCountry,
  formatUsd,
  formatExactMoney,
  propertyManagerFromRecord,
  getPropertyListingMode,
} from '../data/countries';
import {
  getPropertyPlatformPrice,
  getPropertyListerPrice,
  isFirestoreListingId,
  getPropertyListedAt,
  formatPropertyDateTime,
  propertyMatchesListedDateFilter,
} from './properties';
import { formatSubmissionListingModeLabel } from './submissions';

export const listingMatchesListedDateFilter = propertyMatchesListedDateFilter;

export const LIVE_LISTINGS_METRICS = [
  { key: 'fractionalBuy', label: 'Fractional buy' },
  { key: 'forRent', label: 'For rent' },
  { key: 'liveOnSite', label: 'Live on site' },
  { key: 'demoSamples', label: 'Demo samples' },
  { key: 'nowFunding', label: 'Now funding' },
  { key: 'fullyFunded', label: 'Fully funded' },
];

export const LIVE_LISTINGS_CHART_COLORS = {
  fractionalBuy: '#102734',
  forRent: '#d4b06f',
  liveOnSite: '#18384a',
  demoSamples: '#bf9a52',
  nowFunding: '#28a745',
  fullyFunded: '#64748b',
};

export const computeLiveListingsMetrics = (listings) => {
  const rows = listings || [];
  return {
    fractionalBuy: rows.filter((row) => getPropertyListingMode(row) === 'sale').length,
    forRent: rows.filter((row) => getPropertyListingMode(row) === 'rent').length,
    liveOnSite: rows.filter((row) => isFirestoreListingId(row.id)).length,
    demoSamples: rows.filter((row) => !isFirestoreListingId(row.id)).length,
    nowFunding: rows.filter((row) => row.status === 'funding').length,
    fullyFunded: rows.filter((row) => row.status === 'funded').length,
  };
};

const csvEscape = (value) => {
  const s = String(value ?? '');
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
};

const rowToCsvRecord = (property) => {
  const isRent = getPropertyListingMode(property) === 'rent';
  const progress = property.shares
    ? ((Number(property.sharesSold) / Number(property.shares)) * 100).toFixed(1)
    : '0';
  const pm = propertyManagerFromRecord(property);
  const listed = getPropertyListedAt(property);
  const platformPrice = getPropertyPlatformPrice(property);
  const listerPrice = getPropertyListerPrice(property);

  const listedIso = listed ? listed.toISOString().slice(0, 10) : '';
  const listedMonth = listed
    ? `${listed.getFullYear()}-${String(listed.getMonth() + 1).padStart(2, '0')}`
    : '';

  return {
    id: property.id || '',
    title: property.title || '',
    location: property.location || '',
    country: property.country ? (getCountry(property.country).name || property.country) : '',
    countryCode: property.country || '',
    source: isFirestoreListingId(property.id) ? 'Live' : 'Demo',
    listingMode: getPropertyListingMode(property),
    listingModeLabel: formatSubmissionListingModeLabel(property, { short: false }),
    type: property.type || '',
    platformPriceUsd: isRent ? '' : platformPrice,
    listerPriceUsd: !isRent && listerPrice > 0 ? listerPrice : '',
    monthlyRentUsd: isRent && Number(property.monthlyRent) > 0 ? Number(property.monthlyRent) : '',
    targetDisplay: isRent && Number(property.monthlyRent) > 0
      ? `${formatUsd(Number(property.monthlyRent))}/mo`
      : formatExactMoney(platformPrice, property.country || 'US'),
    shares: property.shares ?? '',
    sharesSold: property.sharesSold ?? '',
    raisedPercent: isRent ? '' : progress,
    status: property.status || '',
    manager: pm.name || '',
    listedAt: listed ? formatPropertyDateTime(listed) : '',
    listedAtIso: listedIso,
    listedMonth,
  };
};

export const downloadLiveListingsCsv = (rows, { filenamePrefix = 'coown-live-listings' } = {}) => {
  const headers = [
    'id',
    'title',
    'location',
    'country',
    'countryCode',
    'source',
    'listingMode',
    'listingModeLabel',
    'type',
    'platformPriceUsd',
    'listerPriceUsd',
    'monthlyRentUsd',
    'targetDisplay',
    'shares',
    'sharesSold',
    'raisedPercent',
    'status',
    'manager',
    'listedAt',
    'listedAtIso',
    'listedMonth',
  ];
  const records = (rows || []).map(rowToCsvRecord);
  const lines = [headers.map(csvEscape).join(',')];
  records.forEach((record) => {
    lines.push(headers.map((header) => csvEscape(record[header])).join(','));
  });
  const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' });
  const stamp = new Date().toISOString().slice(0, 10);
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${filenamePrefix}-${stamp}.csv`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};
